import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProductV2Service } from '../../core/services/product-v2.service';
import { ToastService } from '../../shared/services/toast.service';
import {
  ProductV2,
  ProductV2Variation,
} from '../../core/models/product-v2.model';
import { UrlService } from '../../core/services/url.service';
import { SellerService } from '../../core/services/seller.service';
import { Seller, SellerResponse } from '../../core/models/seller.model';
import { SellerCodeStorageService } from '../../core/services/seller-code-storage.service';
import { buildRetryCartQueryParams } from '../../core/utils/order-retry-cart.util';
import {
  isRegistryDomainProductUuid,
  isRegistryDomainVariationUuid,
} from '../../core/utils/cart-domain-suggestion-rules';
import {
  isBlipGoProduct,
  isBlipGoVariationUuid,
} from '../../core/constants/blip-go.constants';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { CurrencyBRLPipe } from '../../shared/pipes/currency-brl.pipe';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { Icon } from '../../core/models/icon.model';

interface CatalogItem {
  productName: string;
  variationName: string;
  productUuid: string;
  variationUuid: string;
  product: ProductV2;
  variation?: ProductV2Variation;
  quantity: number;
  generatedUrl: string;
  copied?: boolean;
  justAdded?: boolean;
}

interface SellerCartItem {
  productId: string;
  variationId: string;
  quantity: number;
  name: string;
  variationName: string;
  unitPrice: number;
  paymentRecurrence?: string;
}

interface SellerCartGroup {
  productId: string;
  name: string;
  items: SellerCartItem[];
}

const PERMITTED_PLAN_NAMES = new Set<string>([
  'Pacote - Basic',
  'Pacote - Bronze',
  'Pacote - Silver',
  'Package - Basic',
  'Package - Bronze',
  'Package - Silver',
  'Paquete - Básico',
  'Paquete - Bronce',
  'Paquete - Plata',
]);

function isSpecialProduct(name?: string): boolean {
  if (!name) return false;
  const normalizedName = name.trim().toLowerCase();
  return (
    normalizedName === 'starbem' ||
    normalizedName === 'telemedicina' ||
    normalizedName === 'telemedicine'
  );
}

@Component({
  selector: 'umkt-catalog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CustomInputComponent,
    LoadingComponent,
    IconComponent,
    CurrencyBRLPipe,
    I18nPipe,
  ],
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
})
export class CatalogComponent implements OnInit, OnDestroy {
  catalogItems: CatalogItem[] = [];
  filteredItems: CatalogItem[] = [];
  loading = true;
  error: string | null = null;
  searchTerm = '';
  selectedCategory = '';
  categories: string[] = [];

  // Estado separado do modo vendedor
  isSellerRoute = false;
  sellerMode = false;
  sellerCartExpanded = false;
  sellerCode = '';
  seller: Seller | null = null;
  sellerLookupError: string | null = null;
  sellerLookupLoading = false;
  cartItems: SellerCartItem[] = [];
  finalizeUrl = '';
  finalizeUrlCopied = false;

  private destroy$ = new Subject<void>();
  private lastLookedUpCode: string | null = null;
  private readonly BLIP_GO_SINGLE_PLAN_TOAST =
    'Você só pode adicionar 1 plano Blip Go ao carrinho.';
  private readonly route = inject(ActivatedRoute);
  private productV2Service = inject(ProductV2Service);
  private toastService = inject(ToastService);
  private urlService = inject(UrlService);
  private sellerService = inject(SellerService);
  private sellerCodeStorage = inject(SellerCodeStorageService);
  private i18nService = inject(I18nService);


  ngOnInit(): void {
    this.isSellerRoute = this.route.snapshot.data['sellerMode'] === true;
    this.sellerMode = this.isSellerRoute;

    this.loadProducts();

    this.i18nService
      .getCurrentLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadProducts();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasValidSeller(): boolean {
    return (
      !!this.seller &&
      !this.sellerLookupError &&
      !this.sellerLookupLoading &&
      this.sanitizeSellerCode(this.sellerCode).length === 5
    );
  }

  get canFinalize(): boolean {
    return this.hasValidSeller && this.cartItems.length > 0;
  }

  get showSellerCodeRequiredError(): boolean {
    return (
      this.sellerMode &&
      this.cartItems.length > 0 &&
      !this.hasValidSeller &&
      !this.sellerLookupLoading
    );
  }

  get totalCartItems(): number {
    return this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  get sellerCartGroups(): SellerCartGroup[] {
    const groups = new Map<string, SellerCartGroup>();

    this.cartItems.forEach(item => {
      const existing = groups.get(item.productId);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.set(item.productId, {
          productId: item.productId,
          name: item.name,
          items: [item],
        });
      }
    });

    return Array.from(groups.values());
  }

  getSellerCartItemPrice(item: SellerCartItem): number {
    return (item.unitPrice || 0) * item.quantity;
  }

  getSellerCartItemPeriodKey(item: SellerCartItem): string {
    const isRegistryDomain =
      isRegistryDomainProductUuid(item.productId) ||
      isRegistryDomainVariationUuid(item.variationId);
    return isRegistryDomain
      ? 'components.expansableCard.perYear'
      : 'components.expansableCard.perMonth';
  }

  get sellerCartTotal(): number {
    return this.cartItems.reduce(
      (sum, item) => sum + this.getSellerCartItemPrice(item),
      0
    );
  }

  getSellerProductIcon(): Icon {
    return { name: 'nuvem', type: 'mondrian-icons' };
  }

  toggleSellerMode(): void {
    this.sellerMode = !this.sellerMode;
    if (!this.sellerMode) {
      this.resetSellerModeState();
    }
  }

  toggleSellerCart(): void {
    this.sellerCartExpanded = !this.sellerCartExpanded;
  }

  onSellerCodeChange(value: string): void {
    const sanitized = this.sanitizeSellerCode(value);
    if (sanitized !== this.sellerCode) {
      this.sellerCode = sanitized;
    }
    if (sanitized.length < 5) {
      this.seller = null;
      this.sellerLookupError = null;
      this.lastLookedUpCode = null;
      this.updateFinalizeUrl();
      return;
    }

    this.lookupSeller();
  }

  onSellerCodeBlur(): void {
    this.lookupSeller();
  }

  lookupSellerFromSearch(): void {
    this.lookupSeller();
  }

  private sanitizeSellerCode(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .substring(0, 5);
  }

  private lookupSeller(): void {
    const code = this.sanitizeSellerCode(this.sellerCode);
    this.sellerCode = code;

    if (!code || code.length < 5) {
      this.seller = null;
      this.sellerLookupError = null;
      this.lastLookedUpCode = null;
      return;
    }

    if (this.lastLookedUpCode === code && (this.seller || this.sellerLookupError)) {
      return;
    }

    this.sellerLookupLoading = true;
    this.sellerLookupError = null;
    this.seller = null;
    this.lastLookedUpCode = code;

    this.sellerService
      .getSellerByCode(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: SellerResponse) => {
          this.sellerLookupLoading = false;
          if (!response?.error && response?.data) {
            this.seller = response.data;
            this.sellerLookupError = null;
            this.sellerCodeStorage.set(code);
            this.updateFinalizeUrl();
          } else {
            this.seller = null;
            this.sellerLookupError = this.i18nService.instant(
              'pages.catalog.seller.notFound'
            );
            this.updateFinalizeUrl();
          }
        },
        error: () => {
          this.sellerLookupLoading = false;
          this.seller = null;
          this.sellerLookupError = this.i18nService.instant(
            'pages.catalog.seller.notFound'
          );
          this.updateFinalizeUrl();
        },
      });
  }

  addToSellerCart(item: CatalogItem): void {
    if (!this.sellerMode) {
      return;
    }

    if (!this.hasValidSeller) {
      this.toastService.showWarning(
        this.i18nService.instant('pages.catalog.seller.requireCodeToAdd'),
        5000,
        'top-center'
      );
      return;
    }

    const isBlipGo = this.isBlipGoCatalogItem(item);
    if (isBlipGo && this.hasBlipGoInSellerCart()) {
      this.toastService.showWarning(this.BLIP_GO_SINGLE_PLAN_TOAST, 5000, 'top-center');
      return;
    }

    const quantityToAdd = isBlipGo ? 1 : item.quantity;

    const existing = this.cartItems.find(
      cartItem =>
        cartItem.productId === item.productUuid &&
        cartItem.variationId === item.variationUuid
    );

    if (existing) {
      if (isBlipGo) {
        this.toastService.showWarning(this.BLIP_GO_SINGLE_PLAN_TOAST, 5000, 'top-center');
        return;
      }
      existing.quantity += quantityToAdd;
    } else {
      const basePrice =
        item.variation?.salesPrice ??
        item.variation?.price ??
        item.product.salesPrice ??
        item.product.price ??
        0;
      const paymentRecurrence =
        item.variation?.paymentRecurrence ?? item.product.paymentRecurrence;
      const isRegistryDomain =
        isRegistryDomainProductUuid(item.productUuid) ||
        isRegistryDomainVariationUuid(item.variationUuid);
      const unitPrice =
        paymentRecurrence === 'annual' && !isRegistryDomain
          ? Math.round((basePrice / 12) * 100) / 100
          : basePrice;

      this.cartItems = [
        ...this.cartItems,
        {
          productId: item.productUuid,
          variationId: item.variationUuid,
          quantity: quantityToAdd,
          name: item.productName,
          variationName: item.variationName,
          unitPrice,
          paymentRecurrence,
        },
      ];
    }

    item.justAdded = true;
    setTimeout(() => {
      item.justAdded = false;
    }, 1200);

    this.updateFinalizeUrl();
    this.toastService.showSuccess(
      this.i18nService.instant('pages.catalog.seller.addedToCart')
    );
  }

  isBlipGoCatalogItem(item: CatalogItem | SellerCartItem): boolean {
    if ('product' in item) {
      return (
        isBlipGoProduct(item.product) ||
        isBlipGoVariationUuid(item.variationUuid)
      );
    }

    return (
      isBlipGoProduct({ uuid: item.productId }) ||
      isBlipGoVariationUuid(item.variationId)
    );
  }

  private hasBlipGoInSellerCart(): boolean {
    return this.cartItems.some(cartItem => this.isBlipGoCatalogItem(cartItem));
  }

  removeFromSellerCart(item: SellerCartItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const nextItems = this.cartItems.filter(
      cartItem =>
        !(
          cartItem.productId === item.productId &&
          cartItem.variationId === item.variationId
        )
    );

    if (nextItems.length === this.cartItems.length) {
      return;
    }

    this.cartItems = [...nextItems];
    this.updateFinalizeUrl();
  }

  trackSellerCartGroup(_index: number, group: SellerCartGroup): string {
    return group.productId;
  }

  trackSellerCartItem(_index: number, item: SellerCartItem): string {
    return `${item.productId}:${item.variationId}`;
  }

  incrementSellerCartQuantity(item: SellerCartItem): void {
    if (this.isBlipGoCatalogItem(item)) {
      this.toastService.showWarning(this.BLIP_GO_SINGLE_PLAN_TOAST, 5000, 'top-center');
      return;
    }

    if (item.quantity < 99) {
      item.quantity++;
      this.updateFinalizeUrl();
    }
  }

  decrementSellerCartQuantity(item: SellerCartItem): void {
    if (item.quantity > 1) {
      item.quantity--;
      this.updateFinalizeUrl();
    }
  }

  private updateFinalizeUrl(): void {
    if (!this.hasValidSeller || this.cartItems.length === 0) {
      this.finalizeUrl = '';
      return;
    }

    const lines = this.cartItems.map(item => {
      const hasVariation = item.variationId && item.variationId !== item.productId;
      return {
        productUuid: hasVariation ? item.variationId : item.productId,
        parentProductUuid: hasVariation ? item.productId : null,
        quantity: item.quantity,
      };
    });

    const queryParams = buildRetryCartQueryParams(lines);
    if (!queryParams) {
      this.finalizeUrl = '';
      return;
    }

    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (key === 'add-to-cart') {
        return;
      }
      // buildRetryCartQueryParams já aplica encodeURIComponent em `items`
      if (key === 'items') {
        params.set(key, decodeURIComponent(value));
        return;
      }
      params.set(key, value);
    });
    params.set('sellerCode', this.seller?.sellerCode || this.sellerCode);

    const baseUrl = this.urlService.getBaseUrl();
    this.finalizeUrl = `${baseUrl}/?add-to-cart&${params.toString()}`;
  }

  async copySellerCartLink(): Promise<void> {
    if (!this.canFinalize) {
      if (!this.hasValidSeller) {
        this.toastService.showError(
          this.i18nService.instant('pages.catalog.seller.invalidSellerFinalize')
        );
      } else {
        this.toastService.showError(
          this.i18nService.instant('pages.catalog.seller.emptyCart')
        );
      }
      return;
    }

    this.updateFinalizeUrl();
    if (!this.finalizeUrl) {
      return;
    }

    this.sellerCodeStorage.set(this.sellerCode);

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(this.finalizeUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = this.finalizeUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      this.finalizeUrlCopied = true;
      this.toastService.showSuccess(
        this.i18nService.instant('pages.catalog.copy.success')
      );
      setTimeout(() => {
        this.finalizeUrlCopied = false;
      }, 2000);
    } catch {
      this.toastService.showError(
        this.i18nService.instant('pages.catalog.copy.error')
      );
    }
  }

  private resetSellerModeState(): void {
    this.sellerCode = '';
    this.seller = null;
    this.sellerLookupError = null;
    this.sellerLookupLoading = false;
    this.cartItems = [];
    this.finalizeUrl = '';
    this.finalizeUrlCopied = false;
    this.sellerCartExpanded = false;
    this.lastLookedUpCode = null;
  }

  private loadProducts(): void {
    this.loading = true;
    this.error = null;

    this.productV2Service.getProducts().subscribe({
      next: response => {
        if (response.error) {
          this.error =
            response.msg ||
            this.i18nService.instant('pages.catalog.error.title');
          return;
        }

        this.catalogItems = this.processProducts(response.data);
        this.extractCategories();
        this.filteredItems = [...this.catalogItems];
        this.loading = false;
      },
      error: err => {
        console.error('Erro ao carregar produtos:', err);
        this.error = this.i18nService.instant('pages.catalog.error.generic');
        this.loading = false;
      },
    });
  }

  private processProducts(products: ProductV2[]): CatalogItem[] {
    const items: CatalogItem[] = [];

    products.forEach(product => {
      let variations = product.variations ?? [];

      if (isSpecialProduct(product.name)) {
        variations = variations.filter(v =>
          PERMITTED_PLAN_NAMES.has((v?.name ?? '').trim())
        );
      }

      if (variations.length > 0) {
        variations.forEach(variation => {
          const item: CatalogItem = {
            productName: product.name,
            variationName: variation.name,
            productUuid: product.uuid,
            variationUuid: variation.uuid,
            product: product,
            variation: variation,
            quantity: 1,
            generatedUrl: '',
          };
          item.generatedUrl = this.generateCartLink(item);
          items.push(item);
        });
      } else {
        if (!isSpecialProduct(product.name)) {
          const item: CatalogItem = {
            productName: product.name,
            variationName: this.i18nService.instant(
              'pages.catalog.product.mainVariation'
            ),
            productUuid: product.uuid,
            variationUuid: product.uuid,
            product: product,
            quantity: 1,
            generatedUrl: '',
          };
          item.generatedUrl = this.generateCartLink(item);
          items.push(item);
        }
      }
    });

    return items;
  }

  retry(): void {
    this.loadProducts();
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.filterProducts();
  }

  onCategoryChange(category: string): void {
    this.selectedCategory = category;
    this.filterProducts();
  }

  private extractCategories(): void {
    const categorySet = new Set<string>();
    this.catalogItems.forEach(item => {
      if (item.product.categories && item.product.categories.length > 0) {
        item.product.categories.forEach(category => {
          categorySet.add(category.name);
        });
      }
    });
    this.categories = Array.from(categorySet).sort();
  }

  private filterProducts(): void {
    let filtered = [...this.catalogItems];

    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        item =>
          item.productName.toLowerCase().includes(term) ||
          item.variationName.toLowerCase().includes(term)
      );
    }

    if (this.selectedCategory) {
      filtered = filtered.filter(item =>
        item.product.categories.some(
          category => category.name === this.selectedCategory
        )
      );
    }

    this.filteredItems = filtered;
  }

  incrementQuantity(item: CatalogItem): void {
    if (item.quantity < 99) {
      item.quantity++;
      item.generatedUrl = this.generateCartLink(item);
    }
  }

  decrementQuantity(item: CatalogItem): void {
    if (item.quantity > 1) {
      item.quantity--;
      item.generatedUrl = this.generateCartLink(item);
    }
  }

  generateCartLink(item: CatalogItem): string {
    const baseUrl = this.urlService.getBaseUrl();
    const params = new URLSearchParams();

    params.set('product', item.productUuid);
    if (item.variationUuid !== item.productUuid) {
      params.set('variation', item.variationUuid);
    }
    params.set('open-cart', 'true');
    params.set('quantity', item.quantity.toString());

    return `${baseUrl}/?add-to-cart&${params.toString()}`;
  }

  async copyCartLink(item: CatalogItem): Promise<void> {
    try {
      const link = item.generatedUrl;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        item.copied = true;
        this.toastService.showSuccess(
          this.i18nService.instant('pages.catalog.copy.success')
        );

        setTimeout(() => {
          item.copied = false;
        }, 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
          this.toastService.showError(
            this.i18nService.instant('pages.catalog.copy.error')
          );
        } else {
          item.copied = true;
          this.toastService.showSuccess(
            this.i18nService.instant('pages.catalog.copy.success')
          );

          setTimeout(() => {
            item.copied = false;
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      this.toastService.showError(
        this.i18nService.instant('pages.catalog.copy.error')
      );
    }
  }
}
